import { useEffect, useState, type ReactNode } from 'react';
import { myBlocks } from '../lib/tomo.js';

/**
 * The room's Preferences, reachable from the front page.
 *
 * THE SAME SETTINGS, NOT A SECOND SET. The room is served from this origin
 * under `/live`, so these read and write the exact `localStorage` keys the
 * room does (`tomo-rooms:*` in apps/rooms/src/App.tsx). A console copy with
 * its own keys would be two switches labelled the same thing that disagree.
 * If the room renames a key, rename it here.
 *
 * Per device, like the room: neither setting is stored on the account.
 */
const SHARING_KEY = 'tomo-rooms:sharing';
const COLLAPSE_KEY = 'tomo-rooms:collapse';

export function PreferencesModal({ onClose }: { readonly onClose: () => void }): ReactNode {
  // Sharing is OFF unless somebody said yes; collapsing is ON unless they
  // said no. The defaults are the room's, and asymmetric on purpose.
  const [sharing, setSharing] = useState(() => readFlag(SHARING_KEY) === 'yes');
  const [collapse, setCollapse] = useState(() => readFlag(COLLAPSE_KEY) !== 'no');
  const [blocked, setBlocked] = useState<readonly { uid: string; login: string | null }[] | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    myBlocks.list(controller.signal).then(
      (list) => { if (!controller.signal.aborted) setBlocked(list); },
      // A list that could not load reads as empty. Signed out, it IS empty.
      () => { if (!controller.signal.aborted) setBlocked([]); },
    );
    return () => { controller.abort(); };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const unblock = (uid: string): void => {
    setWorking(uid);
    myBlocks.unblock(uid).then(
      () => { setWorking(null); setBlocked((list) => list?.filter((p) => p.uid !== uid) ?? null); },
      () => { setWorking(null); },
    );
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Preferences"
        onMouseDown={(e) => { e.stopPropagation(); }}
        className="flex max-h-[80vh] w-full max-w-sm flex-col overflow-hidden rounded-xl border border-ink-800 bg-ink-900 text-ink-50"
      >
        <header className="flex items-center justify-between border-b border-ink-800 px-4 py-3">
          <h2 className="text-[14px] font-semibold">Preferences</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preferences"
            className="rounded px-2 text-[13px] text-ink-400 hover:text-ink-50"
          >
            ✕
          </button>
        </header>

        <div className="overflow-y-auto">
          <Toggle
            checked={sharing}
            onChange={() => { const next = !sharing; writeFlag(SHARING_KEY, next); setSharing(next); }}
            label="Share what I am watching"
            detail="Other people in a room see which streams you have on screen. Off by default, and off everywhere until you turn it on."
          />
          <Toggle
            checked={collapse}
            onChange={() => { const next = !collapse; writeFlag(COLLAPSE_KEY, next); setCollapse(next); }}
            label="Collapse repeated messages"
            detail="Forty people posting the same emote becomes one line with a count and who said it. Replies are never collapsed."
          />

          <section className="px-4 py-3">
            <h3 className="text-[13px] font-semibold">Blocked</h3>
            <p className="mt-0.5 text-[12px] leading-4 text-ink-400">
              You will not see their room messages or find them in a room’s
              presence list. This does not cover Twitch chat — block them on
              Twitch for that.
            </p>

            {blocked === null ? (
              <p className="mt-2 text-[12px] text-ink-400">Looking…</p>
            ) : blocked.length === 0 ? (
              <p className="mt-2 text-[12px] text-ink-400">You have not blocked anybody.</p>
            ) : (
              <ul className="mt-2 flex flex-col gap-1">
                {blocked.map((person) => (
                  <li key={person.uid} className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[12px] text-ink-200">
                      {person.login ?? <span className="italic text-ink-400">name not recorded</span>}
                    </span>
                    <button
                      type="button"
                      disabled={working === person.uid}
                      onClick={() => { unblock(person.uid); }}
                      className="shrink-0 rounded bg-ink-800 px-2 py-0.5 text-[11px] text-ink-200 hover:text-ink-50 disabled:text-ink-400"
                    >
                      {working === person.uid ? '…' : 'Unblock'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, detail }: {
  readonly checked: boolean;
  readonly onChange: () => void;
  readonly label: string;
  readonly detail: string;
}): ReactNode {
  return (
    <section className="border-b border-ink-800 px-4 py-3">
      <label className="flex cursor-pointer items-start gap-3">
        <input type="checkbox" checked={checked} onChange={onChange} className="mt-0.5 accent-brand-500" />
        <span>
          <span className="block text-[13px] leading-5">{label}</span>
          <span className="block text-[12px] leading-4 text-ink-400">{detail}</span>
        </span>
      </label>
    </section>
  );
}

/** The room stores each as a one-element JSON array: `["yes"]`. */
function readFlag(key: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? '[]');
    return Array.isArray(parsed) && typeof parsed[0] === 'string' ? parsed[0] : undefined;
  } catch {
    return undefined;
  }
}

function writeFlag(key: string, on: boolean): void {
  try {
    window.localStorage.setItem(key, JSON.stringify([on ? 'yes' : 'no']));
  } catch {
    // Storage blocked: the switch still moves for this visit.
  }
}
